import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, LogOut, PawPrint, Upload, Check, X, ImagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";

const TABS = [
  { id: "leads", label: "Leads", testid: ADMIN.tabLeads },
  { id: "reviews", label: "Reviews", testid: ADMIN.tabReviews },
  { id: "suggestions", label: "Suggestions", testid: ADMIN.tabSuggestions },
  { id: "videos", label: "Videos", testid: ADMIN.tabVideos },
  { id: "community-videos", label: "Community Videos", testid: ADMIN.tabCommunityVideos },
  { id: "stories", label: "Stories", testid: ADMIN.tabStories },
  { id: "careers", label: "Careers", testid: ADMIN.tabCareers },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [videos, setVideos] = useState([]);
  const [communityVideos, setCommunityVideos] = useState([]);
  const [stories, setStories] = useState([]);
  const [careers, setCareers] = useState([]);
  const [careerForm, setCareerForm] = useState({ title: "", qualification: "", skills: "", education: "", price: "" });
  const [careerFile, setCareerFile] = useState(null);
  const [careerUploading, setCareerUploading] = useState(false);
  const careerFileInputRef = useRef(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const refresh = () => {
    api.get("/admin/leads").then((r) => setLeads(r.data));
    api.get("/admin/reviews").then((r) => setReviews(r.data));
    api.get("/admin/suggestions").then((r) => setSuggestions(r.data));
    api.get("/admin/videos").then((r) => setVideos(r.data));
    api.get("/admin/community-videos").then((r) => setCommunityVideos(r.data));
    api.get("/admin/stories").then((r) => setStories(r.data));
    api.get("/admin/careers").then((r) => setCareers(r.data));
  };

  useEffect(() => {
    refresh();
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const toggleReview = async (id, approved) => {
    await api.patch(`/admin/reviews/${id}`, { approved });
    refresh();
  };

  const deleteReview = async (id) => {
    await api.delete(`/admin/reviews/${id}`);
    refresh();
  };

  const uploadVideo = async (e) => {
    e.preventDefault();
    if (!title.trim() || !file) {
      toast.error("Please provide a title and select a video file.");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("file", file);
    if (thumbnail) formData.append("thumbnail", thumbnail);
    try {
      await api.post("/admin/videos", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video uploaded!");
      setTitle("");
      setDescription("");
      setFile(null);
      setThumbnail(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
      refresh();
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const deleteVideo = async (id) => {
    await api.delete(`/admin/videos/${id}`);
    refresh();
  };

  const deleteCommunityVideo = async (id) => {
    await api.delete(`/admin/community-videos/${id}`);
    refresh();
  };

  const toggleStory = async (id, approved) => {
    await api.patch(`/admin/stories/${id}`, { approved });
    refresh();
  };

  const deleteStory = async (id) => {
    await api.delete(`/admin/stories/${id}`);
    refresh();
  };

  const uploadCareer = async (e) => {
    e.preventDefault();
    const { title, qualification, skills, education, price } = careerForm;
    if (!title.trim() || !qualification.trim() || !skills.trim() || !education.trim() || !price) {
      toast.error("Please fill in all career fields.");
      return;
    }
    if (!careerFile) {
      toast.error("Please upload the application PDF (with QR code).");
      return;
    }
    setCareerUploading(true);
    const formData = new FormData();
    formData.append("title", title);
    formData.append("qualification", qualification);
    formData.append("skills", skills);
    formData.append("education", education);
    formData.append("price", price);
    formData.append("file", careerFile);
    try {
      await api.post("/admin/careers", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Career listing published!");
      setCareerForm({ title: "", qualification: "", skills: "", education: "", price: "" });
      setCareerFile(null);
      if (careerFileInputRef.current) careerFileInputRef.current.value = "";
      refresh();
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setCareerUploading(false);
    }
  };

  const deleteCareer = async (id) => {
    await api.delete(`/admin/careers/${id}`);
    refresh();
  };

  return (
    <div className="min-h-screen bg-[#0F2520] text-[#F9F8F6]">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <PawPrint className="w-6 h-6 text-[#F4D06F]" />
            <span className="font-display text-xl font-bold">Raijada Admin</span>
          </div>
          <button
            data-testid={ADMIN.logoutBtn}
            onClick={doLogout}
            className="flex items-center gap-2 text-sm text-[#F9F8F6]/70 hover:text-[#F4D06F]"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        <div className="flex gap-2 mb-8 border-b border-[#234B41]">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-semibold rounded-t-md ${
                tab === t.id ? "bg-[#16332C] text-[#F4D06F]" : "text-[#F9F8F6]/50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div className="bg-[#16332C] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#F9F8F6]/50 border-b border-[#234B41]">
                  <th className="p-4">Name</th>
                  <th className="p-4">Mobile</th>
                  <th className="p-4">Source</th>
                  <th className="p-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-[#234B41]/50" data-testid={`admin-lead-row-${l.id}`}>
                    <td className="p-4">{l.name}</td>
                    <td className="p-4">{l.mobile}</td>
                    <td className="p-4">{l.source}</td>
                    <td className="p-4 text-[#F9F8F6]/50">{new Date(l.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr><td className="p-4 text-[#F9F8F6]/40" colSpan={4}>No leads yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "reviews" && (
          <div className="grid gap-4">
            {reviews.map((r) => (
              <div key={r.id} className="bg-[#16332C] rounded-lg p-5 flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold">{r.name} <span className="text-[#F4D06F]">{"★".repeat(r.rating)}</span></p>
                  <p className="text-[#F9F8F6]/70 mt-1">{r.message}</p>
                  <p className={`text-xs mt-2 ${r.approved ? "text-green-400" : "text-yellow-400"}`}>
                    {r.approved ? "Visible on site" : "Hidden"}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={ADMIN.reviewApproveBtn(r.id)}
                    onClick={() => toggleReview(r.id, !r.approved)}
                    className="p-2 rounded-md bg-[#0F2520] hover:bg-[#234B41]"
                    title={r.approved ? "Hide" : "Approve"}
                  >
                    {r.approved ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button
                    data-testid={ADMIN.reviewDeleteBtn(r.id)}
                    onClick={() => deleteReview(r.id)}
                    className="p-2 rounded-md bg-[#0F2520] hover:bg-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {reviews.length === 0 && <p className="text-[#F9F8F6]/40">No reviews yet.</p>}
          </div>
        )}

        {tab === "suggestions" && (
          <div className="grid gap-4">
            {suggestions.map((s) => (
              <div key={s.id} className="bg-[#16332C] rounded-lg p-5">
                <p className="font-semibold">{s.name}</p>
                <p className="text-[#F9F8F6]/70 mt-1">{s.message}</p>
                <p className="text-xs text-[#F9F8F6]/40 mt-2">{new Date(s.created_at).toLocaleString()}</p>
              </div>
            ))}
            {suggestions.length === 0 && <p className="text-[#F9F8F6]/40">No suggestions yet.</p>}
          </div>
        )}

        {tab === "videos" && (
          <div>
            <form onSubmit={uploadVideo} className="bg-[#16332C] rounded-lg p-6 mb-8 grid gap-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  data-testid={ADMIN.videoTitleInput}
                  placeholder="Video Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="bg-[#0F2520] border-[#234B41] text-[#F9F8F6] h-11"
                />
                <div className="flex items-center gap-2">
                  <label className="text-sm text-[#F9F8F6]/60 flex items-center gap-2 shrink-0">
                    <ImagePlus className="w-4 h-4" /> Thumbnail
                  </label>
                  <input
                    data-testid={ADMIN.videoThumbnailInput}
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setThumbnail(e.target.files?.[0] || null)}
                    className="text-sm text-[#F9F8F6]/70 w-full"
                  />
                </div>
              </div>
              <Textarea
                data-testid={ADMIN.videoDescriptionInput}
                placeholder="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-[#0F2520] border-[#234B41] text-[#F9F8F6] min-h-[80px]"
              />
              <div className="flex flex-col md:flex-row gap-4 items-center md:items-end">
                <input
                  data-testid={ADMIN.videoFileInput}
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="text-sm text-[#F9F8F6]/70 w-full"
                />
                <Button
                  data-testid={ADMIN.videoUploadBtn}
                  type="submit"
                  disabled={uploading}
                  className="bg-[#D97736] hover:bg-[#F4D06F] hover:text-[#0F2520] text-white h-11 shrink-0 w-full md:w-auto"
                >
                  <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </form>

            <div className="grid md:grid-cols-3 gap-4">
              {videos.map((v) => (
                <div key={v.id} className="bg-[#16332C] rounded-lg p-4">
                  {v.thumbnail_storage_path && (
                    <img
                      src={`${api.defaults.baseURL}/videos/${v.id}/thumbnail`}
                      alt={v.title}
                      className="w-full aspect-video object-cover rounded-md mb-3"
                    />
                  )}
                  <p className="font-medium mb-1">{v.title}</p>
                  {v.description && <p className="text-xs text-[#F9F8F6]/60 mb-2">{v.description}</p>}
                  <p className={`text-xs mb-3 ${v.is_deleted ? "text-red-400" : "text-green-400"}`}>
                    {v.is_deleted ? "Deleted" : "Live on site"}
                  </p>
                  {!v.is_deleted && (
                    <button
                      data-testid={ADMIN.videoDeleteBtn(v.id)}
                      onClick={() => deleteVideo(v.id)}
                      className="text-sm flex items-center gap-1 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
              ))}
              {videos.length === 0 && <p className="text-[#F9F8F6]/40">No videos uploaded yet.</p>}
            </div>
          </div>
        )}

        {tab === "community-videos" && (
          <div className="grid md:grid-cols-3 gap-4">
            {communityVideos.map((v) => (
              <div key={v.id} className="bg-[#16332C] rounded-lg p-4">
                <p className="font-medium mb-1">{v.pet_name}</p>
                <p className="text-xs text-[#F9F8F6]/60 mb-1">Owner: {v.owner_name}</p>
                <p className="text-xs text-[#F9F8F6]/60 mb-2">Mobile: {v.mobile}</p>
                <p className={`text-xs mb-3 ${v.is_deleted ? "text-red-400" : "text-green-400"}`}>
                  {v.is_deleted ? "Deleted" : "Live on site"}
                </p>
                {!v.is_deleted && (
                  <button
                    data-testid={ADMIN.communityVideoDeleteBtn(v.id)}
                    onClick={() => deleteCommunityVideo(v.id)}
                    className="text-sm flex items-center gap-1 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                )}
              </div>
            ))}
            {communityVideos.length === 0 && <p className="text-[#F9F8F6]/40">No community videos yet.</p>}
          </div>
        )}

        {tab === "stories" && (
          <div className="grid gap-4">
            {stories.map((s) => (
              <div key={s.id} className="bg-[#16332C] rounded-lg p-5 flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold">
                    {s.owner_name} {s.pet_name ? `& ${s.pet_name}` : ""} — {s.mobile}
                  </p>
                  <p className="text-[#F9F8F6]/70 mt-1">{s.story}</p>
                  <p className={`text-xs mt-2 ${s.approved ? "text-green-400" : "text-yellow-400"}`}>
                    {s.approved ? "Visible on site" : "Hidden"}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={ADMIN.storyApproveBtn(s.id)}
                    onClick={() => toggleStory(s.id, !s.approved)}
                    className="p-2 rounded-md bg-[#0F2520] hover:bg-[#234B41]"
                    title={s.approved ? "Hide" : "Approve"}
                  >
                    {s.approved ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button
                    data-testid={ADMIN.storyDeleteBtn(s.id)}
                    onClick={() => deleteStory(s.id)}
                    className="p-2 rounded-md bg-[#0F2520] hover:bg-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {stories.length === 0 && <p className="text-[#F9F8F6]/40">No stories yet.</p>}
          </div>
        )}

        {tab === "careers" && (
          <div>
            <form onSubmit={uploadCareer} className="bg-[#16332C] rounded-lg p-6 mb-8 grid gap-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  data-testid={ADMIN.careerTitleInput}
                  placeholder="Job Title (e.g. Kennel Assistant)"
                  value={careerForm.title}
                  onChange={(e) => setCareerForm((f) => ({ ...f, title: e.target.value }))}
                  className="bg-[#0F2520] border-[#234B41] text-[#F9F8F6] h-11"
                />
                <Input
                  data-testid={ADMIN.careerPriceInput}
                  placeholder="Application Form Price (INR)"
                  type="number"
                  min="1"
                  value={careerForm.price}
                  onChange={(e) => setCareerForm((f) => ({ ...f, price: e.target.value }))}
                  className="bg-[#0F2520] border-[#234B41] text-[#F9F8F6] h-11"
                />
              </div>
              <Textarea
                data-testid={ADMIN.careerQualificationInput}
                placeholder="Qualification required"
                value={careerForm.qualification}
                onChange={(e) => setCareerForm((f) => ({ ...f, qualification: e.target.value }))}
                className="bg-[#0F2520] border-[#234B41] text-[#F9F8F6] min-h-[70px]"
              />
              <Textarea
                data-testid={ADMIN.careerSkillsInput}
                placeholder="Skills required"
                value={careerForm.skills}
                onChange={(e) => setCareerForm((f) => ({ ...f, skills: e.target.value }))}
                className="bg-[#0F2520] border-[#234B41] text-[#F9F8F6] min-h-[70px]"
              />
              <Textarea
                data-testid={ADMIN.careerEducationInput}
                placeholder="Education required"
                value={careerForm.education}
                onChange={(e) => setCareerForm((f) => ({ ...f, education: e.target.value }))}
                className="bg-[#0F2520] border-[#234B41] text-[#F9F8F6] min-h-[70px]"
              />
              <div className="flex flex-col md:flex-row gap-4 items-center md:items-end">
                <div className="flex-1 w-full">
                  <label className="text-xs text-[#F9F8F6]/60 flex items-center gap-2 mb-1">
                    Application Form PDF (with QR code)
                  </label>
                  <input
                    data-testid={ADMIN.careerFileInput}
                    ref={careerFileInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setCareerFile(e.target.files?.[0] || null)}
                    className="text-sm text-[#F9F8F6]/70 w-full"
                  />
                </div>
                <Button
                  data-testid={ADMIN.careerUploadBtn}
                  type="submit"
                  disabled={careerUploading}
                  className="bg-[#D97736] hover:bg-[#F4D06F] hover:text-[#0F2520] text-white h-11 shrink-0 w-full md:w-auto"
                >
                  <Upload className="w-4 h-4 mr-2" /> {careerUploading ? "Publishing..." : "Publish Career"}
                </Button>
              </div>
            </form>

            <div className="grid md:grid-cols-3 gap-4">
              {careers.map((c) => (
                <div key={c.id} className="bg-[#16332C] rounded-lg p-4">
                  <p className="font-medium mb-1">{c.title}</p>
                  <p className="text-xs text-[#F9F8F6]/60 mb-2">₹{c.price}</p>
                  <p className={`text-xs mb-3 ${c.is_deleted ? "text-red-400" : "text-green-400"}`}>
                    {c.is_deleted ? "Deleted" : "Live on site"}
                  </p>
                  {!c.is_deleted && (
                    <button
                      data-testid={ADMIN.careerDeleteBtn(c.id)}
                      onClick={() => deleteCareer(c.id)}
                      className="text-sm flex items-center gap-1 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
              ))}
              {careers.length === 0 && <p className="text-[#F9F8F6]/40">No careers published yet.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
